import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type {
  Student,
  TimeslotPreference,
  DayOfWeek,
  LectureLength,
} from "../types";
import { preferencesApi, lectureLengthsApi } from "../api/client";
import "./StudentPreferences.css";

interface StudentPreferencesProps {
  students: Student[];
  onPreferencesChange: () => void;
  selectedStudent?: Student | null;
  hideHeader?: boolean;
}

const DAYS: DayOfWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
];

export function StudentPreferences({
  students,
  onPreferencesChange,
  selectedStudent: selectedStudentProp = null,
  hideHeader = false,
}: StudentPreferencesProps) {
  const { t } = useTranslation();
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(
    selectedStudentProp,
  );
  const [preferences, setPreferences] = useState<TimeslotPreference[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [day, setDay] = useState<DayOfWeek>("monday");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("09:30");
  const [type, setType] = useState<"preferred" | "alternative" | "block_hour">(
    "preferred",
  );
  const [lectureLengths, setLectureLengths] = useState<LectureLength[]>([]);
  const [selectedLectureLengthId, setSelectedLectureLengthId] = useState<
    number | ""
  >("");

  useEffect(() => {
    setSelectedStudent(selectedStudentProp || null);
  }, [selectedStudentProp]);

  useEffect(() => {
    if (selectedStudent) {
      loadPreferences(selectedStudent.id);
    }
  }, [selectedStudent]);
  // Load lecture lengths when a student is selected (based on student's school)
  useEffect(() => {
    const loadLectureLengths = async () => {
      if (!selectedStudent) return;
      try {
        const res = await lectureLengthsApi.getBySchool(
          selectedStudent.school_id,
        );
        setLectureLengths(res.data);
      } catch (e) {
        console.error("Failed to load lecture lengths", e);
      }
    };
    loadLectureLengths();
  }, [selectedStudent]);

  const loadPreferences = async (studentId: number) => {
    setLoading(true);
    try {
      const response = await preferencesApi.getByStudent(studentId);
      setPreferences(response.data);
    } catch (error) {
      console.error("Failed to load preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setDay("monday");
    setStartTime("09:00");
    setEndTime("09:30");
    setType("preferred");
    setError(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent) {
      setError(t("preferences.selectStudent"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Derive duration based on user choice:
      // 1) If a lecture length is selected, use its duration
      // 2) Otherwise, compute from start/end
      let durationMin: number;
      if (selectedLectureLengthId) {
        const ll = lectureLengths.find((l) => l.id === selectedLectureLengthId);
        durationMin = ll ? ll.duration : 0;
      } else {
        const [sh, sm] = startTime.split(":").map(Number);
        const [eh, em] = endTime.split(":").map(Number);
        durationMin = eh * 60 + em - (sh * 60 + sm);
      }
      if (!durationMin || durationMin <= 0) {
        setError(t("timeslots.timeRangeInvalid"));
        return;
      }
      await preferencesApi.create({
        student_id: selectedStudent.id,
        day,
        start_time: startTime,
        duration: durationMin,
        type,
      });
      await loadPreferences(selectedStudent.id);
      onPreferencesChange();
      handleCloseModal();
    } catch (err: any) {
      console.error("Failed to create preference:", err);
      const errorMsg =
        err?.response?.data?.detail || err?.message || t("common.error");
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (prefId: number) => {
    if (!window.confirm(t("preferences.confirmDelete"))) {
      return;
    }

    setLoading(true);
    try {
      await preferencesApi.delete(prefId);
      if (selectedStudent) {
        await loadPreferences(selectedStudent.id);
      }
      onPreferencesChange();
    } catch (error) {
      console.error("Failed to delete preference:", error);
      alert(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const getPreferenceTypeLabel = (type: string) => {
    switch (type) {
      case "preferred":
        return t("preferences.preferred");
      case "alternative":
        return t("preferences.alternative");
      case "block_hour":
        return t("preferences.blockHour");
      default:
        return type;
    }
  };

  const getPreferenceIcon = (type: string) => {
    switch (type) {
      case "preferred":
        return "⭐";
      case "alternative":
        return "⚠️";
      case "block_hour":
        return "🚫";
      default:
        return "•";
    }
  };

  const getEndTime = (start: string, durationMin: number) => {
    // start expected as HH:MM or HH:MM:SS -> take HH:MM
    const hhmm = start.slice(0, 5);
    const [h, m] = hhmm.split(":").map(Number);
    const startTotal = h * 60 + m;
    const endTotal = (startTotal + durationMin) % (24 * 60);
    const eh = Math.floor(endTotal / 60);
    const em = endTotal % 60;
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    return `${pad(eh)}:${pad(em)}`;
  };

  const byDay = (dayKey: DayOfWeek) =>
    preferences
      .filter((p) => p.day === dayKey)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));

  return (
    <div
      className={`student-preferences-container${selectedStudentProp ? " no-selector" : ""}`}
    >
      {!hideHeader && (
        <div className="view-header">
          <h2>{t("preferences.title")}</h2>
        </div>
      )}

      <div className="preferences-layout">
        {!selectedStudentProp && (
          <div className="students-selector">
            <h3>{t("students.title")}</h3>
            {students.length === 0 ? (
              <p className="empty-message">{t("students.noStudents")}</p>
            ) : (
              <div className="students-selector-list">
                {students.map((student) => (
                  <button
                    key={student.id}
                    className={`student-selector-item ${selectedStudent?.id === student.id ? "active" : ""}`}
                    onClick={() => setSelectedStudent(student)}
                  >
                    <div className="student-avatar-small">
                      {student.name.charAt(0).toUpperCase()}
                    </div>
                    <span>{student.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="preferences-content">
          {!selectedStudent ? (
            <div className="empty-state">
              <p>{t("preferences.selectStudentPrompt")}</p>
            </div>
          ) : (
            <>
              <div className="preferences-header">
                <h3>
                  {selectedStudent.name}'s {t("preferences.title")}
                </h3>
                <button className="btn-primary" onClick={handleOpenModal}>
                  + {t("preferences.addPreference")}
                </button>
              </div>

              {/* Mini calendar Monday–Friday */}
              <div className="mini-calendar">
                {DAYS.map((d) => (
                  <div key={d} className="mini-col">
                    <div className="mini-col-header">{t(`days.${d}`)}</div>
                    <div className="mini-col-body">
                      {byDay(d).length === 0 ? (
                        <div className="mini-empty">–</div>
                      ) : (
                        byDay(d).map((pref) => (
                          <div
                            key={pref.id}
                            className={`mini-slot ${pref.type}`}
                          >
                            <span className="mini-time">
                              {pref.start_time.slice(0, 5)}–
                              {getEndTime(pref.start_time, pref.duration)}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {loading && preferences.length === 0 ? (
                <div
                  className="card"
                  style={{ textAlign: "center", padding: "2rem" }}
                >
                  <p>{t("common.loading")}</p>
                </div>
              ) : preferences.length === 0 ? (
                <div className="card empty-state">
                  <p>{t("preferences.noPreferences")}</p>
                  <button className="btn-primary" onClick={handleOpenModal}>
                    {t("preferences.addFirstPreference")}
                  </button>
                </div>
              ) : (
                <div className="preferences-grid">
                  {preferences.map((pref) => (
                    <div
                      key={pref.id}
                      className={`preference-card ${pref.type}`}
                    >
                      <div className="preference-card-header">
                        <span className="preference-type">
                          {getPreferenceIcon(pref.type)}{" "}
                          {getPreferenceTypeLabel(pref.type)}
                        </span>
                        <button
                          className="btn-icon btn-icon-danger"
                          onClick={() => handleDelete(pref.id)}
                          disabled={loading}
                          title={t("common.delete")}
                        >
                          🗑️
                        </button>
                      </div>
                      <div className="preference-details">
                        <div className="preference-day">
                          {t(`days.${pref.day}`)}
                        </div>
                        <div className="preference-time">
                          {pref.start_time.slice(0, 5)}–
                          {getEndTime(pref.start_time, pref.duration)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t("preferences.addPreference")}</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="pref-day">{t("timeslots.day")}</label>
                <select
                  id="pref-day"
                  value={day}
                  onChange={(e) => setDay(e.target.value as DayOfWeek)}
                  disabled={loading}
                >
                  {DAYS.map((d) => (
                    <option key={d} value={d}>
                      {t(`days.${d}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="pref-time">{t("timeslots.startTime")}</label>
                <input
                  id="pref-time"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="pref-end">{t("timeslots.endTime")}</label>
                <input
                  id="pref-end"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="pref-ll">{t("lectureLengths.title")}</label>
                <select
                  id="pref-ll"
                  value={selectedLectureLengthId}
                  onChange={(e) =>
                    setSelectedLectureLengthId(
                      e.target.value ? Number(e.target.value) : "",
                    )
                  }
                  disabled={loading}
                >
                  <option value="">{t("common.none")}</option>
                  {lectureLengths.map((ll) => (
                    <option key={ll.id} value={ll.id}>
                      {ll.name} ({ll.duration} {t("lectureLengths.minutes")})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="pref-type">
                  {t("preferences.preferenceType")}
                </label>
                <select
                  id="pref-type"
                  value={type}
                  onChange={(e) =>
                    setType(
                      e.target.value as
                        | "preferred"
                        | "alternative"
                        | "block_hour",
                    )
                  }
                  disabled={loading}
                >
                  <option value="preferred">
                    ⭐ {t("preferences.preferred")}
                  </option>
                  <option value="alternative">
                    ⚠️ {t("preferences.alternative")}
                  </option>
                  <option value="block_hour">
                    🚫 {t("preferences.blockHour")}
                  </option>
                </select>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseModal}
                  disabled={loading}
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? t("common.saving") : t("common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
